import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../shared/utils/firebase';
import { whereLocation } from '../../auth/managedLocations';
import { toDate } from '../../../shared/utils/dateHelpers';

export type ClientDirectoryRole = 'USER' | 'MANAGER' | 'ADMIN';

export type ClientDirectoryUserContext = {
  userId?: string;
  userRole: ClientDirectoryRole;
  coachId?: string;
  assignedLocations?: string[];
};

export type UpcomingAppointment = {
  coach?: string;
  location?: string;
  date?: { value?: string };
};

export type ClientDirectoryClient = {
  id: string;
  client_id: string;
  client_name: string;
  email: string;
  lead_coach_id: string;
  lead_coach_name: string;
  primary_location: string;
  active_programme: boolean;
  conversion_status: boolean | null;
  report_status: string | null;
  collaborating_coach_ids: string[];
  first_session_ts: number | null;
  last_visit_ts: number | null;
  upcoming_appointments: UpcomingAppointment[];
  pre_health_consult_data_submitted: boolean | null;
  health_consult_data_submitted: boolean | null;
  client_intake_data_submitted: boolean | null;
  assessment_inbody_data_submitted: boolean | null;
  assessment_nutrition_data_submitted: boolean | null;
  assessment_metabolic_data_submitted: boolean | null;
  assessment_movement_data_submitted: boolean | null;
  assessment_training_data_submitted: boolean | null;
};

type ClientDirectoryCacheEntry = {
  timestamp: number;
  clients: ClientDirectoryClient[];
};

const CACHE_TTL_MS = 4 * 60 * 60 * 1000;
const memoryCache = new Map<string, ClientDirectoryCacheEntry>();

const buildCacheKey = (ctx: ClientDirectoryUserContext): string => {
  const scope =
    ctx.userRole === 'ADMIN'
      ? 'all'
      : ctx.userRole === 'MANAGER'
        ? `location:${ctx.assignedLocations?.join('|') || 'unknown'}`
        : `coach:${ctx.coachId || 'unknown'}`;

  return ['clientDirectory', 'v1', ctx.userId || 'anon', ctx.userRole, scope].join(':');
};

const getLocalStorageKey = (cacheKey: string) => `clientDirectoryCache:${cacheKey}`;

const isFresh = (timestamp: number) => Date.now() - timestamp < CACHE_TTL_MS;

const readFromLocalStorage = (cacheKey: string): ClientDirectoryCacheEntry | null => {
  try {
    const raw = localStorage.getItem(getLocalStorageKey(cacheKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ClientDirectoryCacheEntry;
    if (!parsed?.timestamp || !Array.isArray(parsed.clients)) return null;
    if (!isFresh(parsed.timestamp)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeToLocalStorage = (cacheKey: string, entry: ClientDirectoryCacheEntry) => {
  try {
    localStorage.setItem(getLocalStorageKey(cacheKey), JSON.stringify(entry));
  } catch (error) {
    console.warn('Failed to persist client directory cache', error);
  }
};

const safeBoolean = (value: unknown): boolean | null => (typeof value === 'boolean' ? value : null);

const normalizeAppointments = (value: unknown): UpcomingAppointment[] => {
  if (!Array.isArray(value)) return [];

  const parsed = value
    .map((appointment: any): UpcomingAppointment | null => {
      const dateValue = appointment?.date?.value;
      if (!dateValue) return null;
      return {
        coach: typeof appointment?.coach === 'string' ? appointment.coach : undefined,
        location: typeof appointment?.location === 'string' ? appointment.location : undefined,
        date: { value: String(dateValue) },
      };
    })
    .filter((appointment): appointment is UpcomingAppointment => appointment !== null);

  parsed.sort((a, b) => {
    const dateA = toDate(a.date?.value)?.getTime() ?? Number.POSITIVE_INFINITY;
    const dateB = toDate(b.date?.value)?.getTime() ?? Number.POSITIVE_INFINITY;
    return dateA - dateB;
  });

  return parsed.slice(0, 10);
};

const computeLastVisitTimestamp = (visitDates: unknown): number | null => {
  if (!Array.isArray(visitDates)) return null;

  let maxTs: number | null = null;
  for (const raw of visitDates) {
    const date = toDate(raw);
    if (!date) continue;
    const ts = date.getTime();
    if (maxTs === null || ts > maxTs) maxTs = ts;
  }

  return maxTs;
};

const normalizeClientDoc = (docSnap: any): ClientDirectoryClient => {
  const data = docSnap.data() as any;

  return {
    id: docSnap.id,
    client_id: String(data?.client_id ?? docSnap.id),
    client_name: String(data?.client_name ?? ''),
    email: String(data?.email ?? ''),
    lead_coach_id: data?.lead_coach_id != null ? String(data.lead_coach_id) : '',
    lead_coach_name: String(data?.lead_coach_name ?? ''),
    primary_location: String(data?.primary_location ?? ''),
    active_programme: Boolean(data?.active_programme),
    conversion_status: typeof data?.conversion_status === 'boolean' ? data.conversion_status : null,
    report_status: typeof data?.report_status === 'string' ? data.report_status : null,
    collaborating_coach_ids: Array.isArray(data?.collaborating_coach_ids)
      ? data.collaborating_coach_ids.map((id: unknown) => String(id))
      : [],
    first_session_ts: toDate(data?.first_session_date)?.getTime() ?? null,
    last_visit_ts: computeLastVisitTimestamp(data?.visit_dates),
    upcoming_appointments: normalizeAppointments(data?.upcoming_appointments),
    pre_health_consult_data_submitted: safeBoolean(data?.pre_health_consult_data_submitted),
    health_consult_data_submitted: safeBoolean(data?.health_consult_data_submitted),
    client_intake_data_submitted: safeBoolean(data?.client_intake_data_submitted),
    assessment_inbody_data_submitted: safeBoolean(data?.assessment_inbody_data_submitted),
    assessment_nutrition_data_submitted: safeBoolean(data?.assessment_nutrition_data_submitted),
    assessment_metabolic_data_submitted: safeBoolean(data?.assessment_metabolic_data_submitted),
    assessment_movement_data_submitted: safeBoolean(data?.assessment_movement_data_submitted),
    assessment_training_data_submitted: safeBoolean(data?.assessment_training_data_submitted),
  };
};

const sortClients = (clients: ClientDirectoryClient[]) => {
  clients.sort((a, b) => a.client_name.localeCompare(b.client_name));
};

const fetchClients = async (ctx: ClientDirectoryUserContext): Promise<ClientDirectoryClient[]> => {
  const clientsRef = collection(db, 'bigquery_cache/client_data/comprehensive_details');

  if (ctx.userRole === 'ADMIN') {
    const snapshot = await getDocs(query(clientsRef));
    const clients = snapshot.docs.map(normalizeClientDoc);
    sortClients(clients);
    return clients;
  }

  if (ctx.userRole === 'MANAGER') {
    if (!ctx.assignedLocations?.length) return [];
    const snapshot = await getDocs(query(clientsRef, whereLocation('primary_location', ctx.assignedLocations)));
    const clients = snapshot.docs.map(normalizeClientDoc);
    sortClients(clients);
    return clients;
  }

  if (ctx.userRole === 'USER') {
    if (!ctx.coachId) return [];

    const coachIdString = String(ctx.coachId);
    const coachIdNumber = Number(ctx.coachId);
    const hasNumericCoachId = !Number.isNaN(coachIdNumber);

    const leadCoachQueryString = query(clientsRef, where('lead_coach_id', '==', coachIdString));
    const leadCoachQueryNumber = hasNumericCoachId
      ? query(clientsRef, where('lead_coach_id', '==', coachIdNumber))
      : null;
    const collaboratorQuery = query(clientsRef, where('collaborating_coach_ids', 'array-contains', coachIdString));

    const [leadStringSnapshot, leadNumberSnapshot, collaboratorSnapshot] = await Promise.all([
      getDocs(leadCoachQueryString),
      leadCoachQueryNumber ? getDocs(leadCoachQueryNumber) : Promise.resolve(null),
      getDocs(collaboratorQuery),
    ]);

    const byId = new Map<string, ClientDirectoryClient>();
    leadStringSnapshot.docs.forEach(docSnap => byId.set(docSnap.id, normalizeClientDoc(docSnap)));
    leadNumberSnapshot?.docs.forEach(docSnap => byId.set(docSnap.id, normalizeClientDoc(docSnap)));

    const leadClientIds = new Set(byId.keys());
    collaboratorSnapshot.docs.forEach(docSnap => {
      if (leadClientIds.has(docSnap.id)) return;
      byId.set(docSnap.id, normalizeClientDoc(docSnap));
    });

    const clients = Array.from(byId.values());
    sortClients(clients);
    return clients;
  }

  return [];
};

export class ClientDirectoryService {
  static getCacheKey(ctx: ClientDirectoryUserContext): string {
    return buildCacheKey(ctx);
  }

  static readCache(ctx: ClientDirectoryUserContext): ClientDirectoryCacheEntry | null {
    const cacheKey = buildCacheKey(ctx);
    const memoryEntry = memoryCache.get(cacheKey);
    if (memoryEntry && isFresh(memoryEntry.timestamp)) {
      return memoryEntry;
    }

    const localEntry = readFromLocalStorage(cacheKey);
    if (!localEntry) return null;

    memoryCache.set(cacheKey, localEntry);
    return localEntry;
  }

  static invalidate(ctx: ClientDirectoryUserContext): void {
    const cacheKey = buildCacheKey(ctx);
    memoryCache.delete(cacheKey);
    try {
      localStorage.removeItem(getLocalStorageKey(cacheKey));
    } catch {
      // ignore
    }
  }

  static async getClients(
    ctx: ClientDirectoryUserContext,
    options?: { forceRefresh?: boolean }
  ): Promise<ClientDirectoryClient[]> {
    const cacheKey = buildCacheKey(ctx);

    if (!options?.forceRefresh) {
      const memoryEntry = memoryCache.get(cacheKey);
      if (memoryEntry && isFresh(memoryEntry.timestamp)) {
        return memoryEntry.clients;
      }

      const localEntry = readFromLocalStorage(cacheKey);
      if (localEntry) {
        memoryCache.set(cacheKey, localEntry);
        return localEntry.clients;
      }
    }

    const clients = await fetchClients(ctx);
    const entry: ClientDirectoryCacheEntry = { timestamp: Date.now(), clients };
    memoryCache.set(cacheKey, entry);
    writeToLocalStorage(cacheKey, entry);
    return clients;
  }
}

